// Thin fetch wrapper. SPEC-009 §4.3 — every request goes credentials:include
// so the httpOnly access cookie is sent automatically. On a 401 we trigger a
// silent /v1/auth/refresh once and retry the original request.

const BASE = ""; // empty: vite proxies /v1/* + /healthz to backend at :18080.

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

type FetchOpts = RequestInit & { skipRefresh?: boolean };

let refreshing: Promise<boolean> | null = null;

// Exported so non-fetch consumers (the SSE EventSource hook) share the SAME
// single-flight refresh. Sharing the mutex matters: it stops the api client and
// the SSE hook from POSTing /v1/auth/refresh concurrently, which under refresh-
// token family rotation could make one call present an already-rotated token →
// reuse detection revokes the whole family → session dies. Returns res.ok.
export async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Mutex resets after one cycle so subsequent 401s in a fresh request
      // window can try again.
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, init: FetchOpts = {}): Promise<T> {
  const url = `${BASE}${path}`;
  const opts: RequestInit = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  };

  let res = await fetch(url, opts);

  if (res.status === 401 && !init.skipRefresh) {
    const ok = await tryRefresh();
    if (ok) {
      res = await fetch(url, opts);
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJSON(text) : null;

  if (!res.ok) {
    const code = (body && typeof body === "object" && (body as Record<string, unknown>).code) || "internal";
    const msg = (body && typeof body === "object" && (body as Record<string, unknown>).message) || res.statusText;
    throw new ApiError(res.status, String(code), String(msg));
  }
  return body as T;
}

function safeJSON(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
